'use client';

import { ContextualPopup } from '@ui/components';
import { debounce, throttle } from 'lodash';
import type { FC, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { useDataPopup } from '../hooks/useDataPopup';
import { useTruth } from '../hooks/useTruth';
import { messages } from '../messages';

interface ConditionDeviceOrientationProps extends PropsWithChildren {
  still?: string;
  moving?: string;
}

export const ConditionDeviceOrientation: FC<
  ConditionDeviceOrientationProps
> = ({ still, moving, children }) => {
  const { dataBlocks } = useTruth();
  const { deviceOrientation } = dataBlocks;
  const { DataPopup } = useDataPopup();

  const [handle, setHandle] = useState<React.ReactNode>(children);

  const updateHandler = (text: React.ReactNode) => {
    setHandle(text);
  };

  useEffect(() => {
    let lastOrientation = [0, 0, 0];

    const deviceOrientationHandler = throttle(
      ({ alpha = 0, gamma = 0, beta = 0 }: DeviceOrientationEvent) => {
        const a = alpha || 0;
        const g = gamma || 0;
        const b = beta || 0;

        const minDelta = 0.5;

        if (
          Math.abs(lastOrientation[0] - a) > minDelta ||
          Math.abs(lastOrientation[1] - g) > minDelta ||
          Math.abs(lastOrientation[2] - b) > minDelta
        ) {
          updateHandler(moving);
        } else {
          debounce(() => {
            updateHandler(still);
          }, 1000);
        }

        lastOrientation = [a, g, b];
      },
      250
    );

    addEventListener('deviceorientation', deviceOrientationHandler, true);

    return () => {
      removeEventListener('deviceorientation', deviceOrientationHandler, true);
    };
  }, [still, moving]);

  return (
    <DataPopup
      handle={handle}
      dataVisualisation={<ContextualPopup.GyroscopeVisualisation />}
      condition={{
        title: messages['condition.deviceOrientation'],
        options: [
          {
            title: messages['condition.deviceOrientation.still'],
            value: still,
          },
          {
            title: messages['condition.deviceOrientation.moving'],
            value: moving,
          },
          {
            title: messages['condition.deviceOrientation.unknown'],
            value: children?.toString() || '',
          },
        ],
      }}
      dataBlocks={{ deviceOrientation }}
    />
  );
};
